import { apiGet } from './api-service';
import { OrganogramRecord, Employee } from '../models/DSF/OrganogramAndAppointments';
import { Node, Edge } from 'reactflow';

export async function getOrganogramData(clientId: string, siteId: string): Promise<OrganogramRecord> {
  const response = await apiGet('open', 'site-organogram', {
    clientId,
    siteId
  });
  return response;
}

function generatePageStyles() {
  return `
    @page {
      margin: 20mm !important;
      size: A4 portrait !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      margin: 0;
      padding: 0;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 12px;
      line-height: 1.4;
    }
    * {
      box-sizing: border-box;
    }
    .page {
      width: 100%;
      min-height: 100vh;
      padding: 0;
      position: relative;
      display: flex;
      flex-direction: column;
    }
    .header {
      display: none;
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 20px;
      flex: 0 0 auto;
    }
    .company-logo {
      height: 50px;
      width: auto;
      max-width: 150px;
      object-fit: contain;
    }
    .header-center {
      flex: 1;
      text-align: center;
      padding: 0 20px;
    }
    .header-title {
      font-size: 18px;
      font-weight: 700;
      margin: 0;
      color: #2c3e50;
      line-height: 1.2;
    }
    .header-subtitle {
      font-size: 14px;
      margin: 4px 0 0 0;
      color: #34495e;
      line-height: 1.2;
      font-weight: 500;
    }
    .header-template {
      font-size: 12px;
      margin: 2px 0 0 0;
      color: #7f8c8d;
      line-height: 1.2;
      font-weight: 400;
    }
    .header-right {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
    }
    .dsf-logo {
      height: 45px;
      width: auto;
      max-width: 90px;
      object-fit: contain;
      background: white;
      padding: 4px;
      border-radius: 6px;
      border: 1px solid #bdc3c7;
    }
    .dsf-text-logo {
      height: 45px;
      width: 80px;
      background: linear-gradient(135deg, #3498db, #2980b9);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      font-weight: bold;
      font-size: 16px;
      letter-spacing: 1px;
      text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
      box-shadow: 0 3px 8px rgba(0,0,0,0.2);
    }
    .footer {
      display: none;
    }
    .footer-left {
      flex: 1;
      text-align: left;
      font-weight: 600;
      font-size: 11px;
      color: #2c3e50;
    }
    .footer-center {
      flex: 1;
      text-align: center;
      font-weight: 500;
      font-size: 10px;
    }
    .footer-right {
      flex: 1;
      text-align: right;
      font-size: 9px;
      line-height: 1.4;
    }
    .content {
      margin-top: 0;
      margin-bottom: 0;
      padding: 20px;
      min-height: auto;
      position: relative;
      z-index: 1;
    }
    .organogram {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 15px;
      padding: 10px 0;
    }
    .org-node {
      width: 100%;
      max-width: 600px;
      margin: 0 auto 15px auto;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .node-button {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 8px 32px rgba(102, 126, 234, 0.25);
      width: 100%;
      color: white;
      text-align: left;
      position: relative;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
    }
    .node-button::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%);
      pointer-events: none;
    }
    .node-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 18px;
      padding-bottom: 15px;
      border-bottom: 1px solid rgba(255,255,255,0.25);
    }
    .node-title {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: white;
      line-height: 1.3;
      text-shadow: 0 2px 4px rgba(0,0,0,0.3);
    }
    .node-badge {
      background: rgba(255,255,255,0.25);
      color: white;
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.1);
    }
    .legal-reference {
      margin: 8px 0 0;
      font-size: 12px;
      color: rgba(255,255,255,0.85);
      font-style: italic;
      font-weight: 300;
    }
    .node-employees {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 15px;
      margin-top: 18px;
    }
    .employee {
      background: rgba(255,255,255,0.18);
      border-radius: 12px;
      padding: 16px;
      backdrop-filter: blur(15px);
      border: 1px solid rgba(255,255,255,0.15);
      transition: all 0.3s ease;
    }
    .employee:hover {
      background: rgba(255,255,255,0.25);
      transform: translateY(-2px);
    }
    .employee-name {
      margin: 0 0 6px 0;
      font-size: 14px;
      font-weight: 600;
      color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    .employee-title {
      margin: 0;
      font-size: 12px;
      color: rgba(255,255,255,0.9);
      font-style: italic;
      font-weight: 400;
    }
    .no-employees {
      text-align: center;
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      font-style: italic;
      padding: 20px;
      background: rgba(255,255,255,0.12);
      border-radius: 12px;
      margin-top: 18px;
      border: 1px solid rgba(255,255,255,0.1);
    }
    
    @media print {
      @page {
        size: A4 portrait !important;
        margin: 20mm !important;
      }
      .header {
        display: flex !important;
        position: fixed !important;
        top: 20mm !important;
        left: 20mm !important;
        right: 20mm !important;
        height: 80px !important;
        padding: 15px 20px !important;
        background: white !important;
        border-bottom: 2px solid #e0e0e0 !important;
        z-index: 1000 !important;
        align-items: center !important;
        justify-content: space-between !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
      }
      .footer {
        display: flex !important;
        position: fixed !important;
        bottom: 20mm !important;
        left: 20mm !important;
        right: 20mm !important;
        height: 60px !important;
        padding: 12px 20px !important;
        background: white !important;
        border-top: 2px solid #e0e0e0 !important;
        z-index: 1000 !important;
        align-items: center !important;
        justify-content: space-between !important;
        font-size: 10px !important;
        color: #5a6c7d !important;
        box-shadow: 0 -2px 8px rgba(0,0,0,0.1) !important;
      }
      .content {
        margin-top: 120px !important;
        margin-bottom: 80px !important;
        padding: 0 20mm !important;
      }
      .org-node {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      .node-button {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;
}

interface OrganogramPdfOptions {
  companyName: string;
  siteName: string;
  companyLogo?: string;
  nodes: Node[];
  edges: Edge[];
  effectiveDate?: Date;
  templateName?: string;
}

function buildOrgHierarchy(nodes: Node[]): string {
  // Generate vertical button-style layout
  const sortedNodes = nodes.sort((a, b) => {
    // Sort by position or creation order - assuming nodes have an order property
    const orderA = a.data.order || 0;
    const orderB = b.data.order || 0;
    return orderA - orderB;
  });

  const nodesHtml = sortedNodes.map(node => {
    const employees = node.data.employees || [];

    return `
      <div class="org-node">
        <div class="node-button">
          <div class="node-header">
            <h3 class="node-title">${node.data.name || node.data.label}</h3>
            <span class="node-badge">${employees.length} Employee${employees.length !== 1 ? 's' : ''}</span>
          </div>
          ${node.data.legalReference ? `
            <p class="legal-reference">Legal Reference: ${node.data.legalReference}</p>
          ` : ''}
          
          ${employees.length > 0 ? `
            <div class="node-employees">
              ${employees.map((emp: Employee) => `
                <div class="employee">
                  <p class="employee-name">${emp.name} ${emp.surname}</p>
                  ${emp.title ? `<p class="employee-title">${emp.title}</p>` : ''}
                </div>
              `).join('')}
            </div>
          ` : '<div class="no-employees">No employees assigned to this position</div>'}
        </div>
      </div>
    `;
  }).join('');

  return nodesHtml;
}

export async function generateOrganogramHTML({
  companyName,
  siteName,
  companyLogo,
  nodes,
  edges,
  effectiveDate = new Date(),
  templateName = "Organogram",
}: OrganogramPdfOptions): Promise<string> {
  // Calculate dates
  const nextRevisionDate = new Date(effectiveDate);
  nextRevisionDate.setFullYear(nextRevisionDate.getFullYear() + 1);
  const currentYear = new Date().getFullYear();
  const documentName = "Site Organogram";
  
  // Multiple DSF logo approaches - try different paths and fallback to text
  const dsfLogoOptions = [
    `${window.location.origin}/DSF_Logo-Low-trimmed2.png`,
    `/DSF_Logo-Low-trimmed2.png`,
    `${window.location.origin}/src/assets/icon/DSF_Logo-Low-trimmed2.png`,
    `/src/assets/icon/DSF_Logo-Low-trimmed2.png`,
    `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCA2MCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjI0IiBmaWxsPSIjMDAwIiByeD0iMyIvPgo8dGV4dCB4PSIzMCIgeT0iMTUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiNGRkYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkRTRjwvdGV4dD4KPC9zdmc+`
  ];

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta name="pdf-page-size" content="A4">
        <meta name="pdf-orientation" content="portrait">
        <meta name="pdf-format" content="A4">
        <title>${companyName} - ${siteName} Safety File - ${templateName}</title>
        <style>${generatePageStyles()}</style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            ${companyLogo ? `<img src="${companyLogo}" alt="${companyName}" class="company-logo"/>` : ''}
          </div>
          <div class="header-center">
            <h1 class="header-title">${companyName}</h1>
            <h2 class="header-subtitle">${siteName} Safety File</h2>
            <div class="header-template">${templateName}</div>
          </div>
          <div class="header-right">
            <img src="${dsfLogoOptions[0]}" alt="DSF Logo" class="dsf-logo" 
                 onerror="this.onerror=null; this.src='${dsfLogoOptions[1]}'; this.onerror=function(){this.onerror=null; this.src='${dsfLogoOptions[2]}'; this.onerror=function(){this.onerror=null; this.src='${dsfLogoOptions[3]}'; this.onerror=function(){this.onerror=null; this.src='${dsfLogoOptions[4]}'; this.onerror=function(){this.style.display='none'; this.nextElementSibling.style.display='flex';}}}}"/>
            <div class="dsf-text-logo" style="display: none;">DSF</div>
          </div>
        </div>

        <div class="page">
          <div class="content">
            <div class="organogram">
              ${buildOrgHierarchy(nodes)}
            </div>
          </div>
        </div>

        <div class="footer">
          <div class="footer-left">${documentName}</div>
          <div class="footer-center">
            Page <span class="page-number">1</span> of <span class="total-pages">1</span>
          </div>
          <div class="footer-right">
            Revision: 01<br/>
            Effective Date: ${effectiveDate.toLocaleDateString()}<br/>
            Next Revision Date: ${nextRevisionDate.toLocaleDateString()}<br/>
            © (2022-${currentYear}) DSF Digital Safety Files (Pty) Ltd
          </div>
        </div>

        <script>
          // Add page numbering support
          function updatePageNumbers() {
            const pageNumbers = document.querySelectorAll('.page-number');
            const totalPages = document.querySelectorAll('.total-pages');
            
            // For PDF generation, we'll assume single page for now
            // This can be enhanced later with proper pagination
            pageNumbers.forEach(el => el.textContent = '1');
            totalPages.forEach(el => el.textContent = '1');
          }
          
          document.addEventListener('DOMContentLoaded', updatePageNumbers);
          window.addEventListener('beforeprint', updatePageNumbers);
        </script>
      </body>
    </html>
  `;

  return html;
}
